import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Landmark, Search, Loader2, CheckCircle, XCircle, DollarSign, Receipt,
  AlertTriangle, Download, Plus, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FALLBACK_TRIPS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function TravellingAdminFinance() {
  const [activeTab, setActiveTab] = useState("claims");
  const [loading, setLoading] = useState(true);
  // Claims pending approval
  const [pendingClaims, setPendingClaims] = useState([]);
  // Recons pending approval
  const [pendingRecons, setPendingRecons] = useState([]);
  // Outstanding balances
  const [balances, setBalances] = useState([]);
  // All trips for overview
  const [allTrips, setAllTrips] = useState([]);

  // Dialogs
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [disburseForm, setDisburseForm] = useState({ payment_reference: "", notes: "" });
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ override_letter_url: "", override_notes: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [claimsRes, reconsRes, balancesRes, tripsRes] = await Promise.all([
        axios.get(`${API}/travelling/requests?status=manager_recommended`, { headers: getHeaders() }),
        axios.get(`${API}/travelling/requests?status=recon_manager_recommended`, { headers: getHeaders() }),
        axios.get(`${API}/travelling/outstanding-balances`, { headers: getHeaders() }),
        axios.get(`${API}/travelling/requests`, { headers: getHeaders() }),
      ]);
      setPendingClaims(claimsRes.data);
      setPendingRecons(reconsRes.data);
      setBalances(balancesRes.data);
      setAllTrips(tripsRes.data);
    } catch {
      const claimTrips = FALLBACK_TRIPS.filter(t => t.status === "manager_recommended" || t.status === "claim_submitted");
      const reconTrips = FALLBACK_TRIPS.filter(t => t.status === "recon_manager_recommended" || t.status === "recon_submitted");
      setPendingClaims(claimsRes?.data?.length > 0 ? claimsRes.data : claimTrips);
      setPendingRecons(reconsRes?.data?.length > 0 ? reconsRes.data : reconTrips);
      setBalances(balancesRes?.data?.length > 0 ? balancesRes.data : FALLBACK_TRIPS.filter(t => t.outstanding_balance_blocked));
      setAllTrips(tripsRes?.data?.length > 0 ? tripsRes.data : FALLBACK_TRIPS);
      toast.error("Failed to load. Showing demo data.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openClaimDetail = async (trip) => {
    setNotes(""); setActiveTab("claims");
    try {
      const res = await axios.get(`${API}/travelling/claims?request_id=${trip.id}`, { headers: getHeaders() });
      setSelected({ trip, claim: res.data[0] || null, type: "claims" });
      setDetailOpen(true);
    } catch { toast.error("Failed"); }
  };

  const openReconDetail = async (trip) => {
    setNotes(""); setActiveTab("recons");
    try {
      const res = await axios.get(`${API}/travelling/reconciliations?request_id=${trip.id}`, { headers: getHeaders() });
      setSelected({ trip, recon: res.data[0] || null, type: "recons" });
      setDetailOpen(true);
    } catch { toast.error("Failed"); }
  };

  const handleApproveClaim = async () => {
    if (!selected?.claim) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/travelling/claims/${selected.claim.id}/finance-approve?approval=approved&notes=${encodeURIComponent(notes)}`,
        {}, { headers: getHeaders() }
      );
      toast.success("Claim approved — ready for disbursement");
      setDetailOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDeclineClaim = async () => {
    if (!selected?.claim) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/travelling/claims/${selected.claim.id}/finance-approve?approval=declined&notes=${encodeURIComponent(notes)}`,
        {}, { headers: getHeaders() }
      );
      toast.success("Claim declined");
      setDetailOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const openDisburse = (claim, trip) => {
    setDisburseForm({ payment_reference: "", notes: "" });
    setSelected({ claim, trip, type: "disburse" });
    setDisburseOpen(true);
  };

  const handleDisburse = async () => {
    if (!disburseForm.payment_reference.trim()) { toast.error("Payment reference required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/travelling/disbursements`, {
        claim_id: selected.claim.id,
        request_id: selected.trip.id,
        amount_disbursed: selected.claim.total_claim_amount,
        currency: selected.trip.currency || "ZAR",
        payment_reference: disburseForm.payment_reference,
        notes: disburseForm.notes,
      }, { headers: getHeaders() });
      toast.success("Disbursement recorded — trip in progress");
      setDisburseOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleApproveRecon = async () => {
    if (!selected?.recon) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/travelling/reconciliations/${selected.recon.id}/finance-approve?approval=approved&notes=${encodeURIComponent(notes)}`,
        {}, { headers: getHeaders() }
      );
      // Close trip
      await axios.put(`${API}/travelling/reconciliations/${selected.recon.id}/close-trip`, {}, { headers: getHeaders() });
      toast.success("Reconciliation approved and trip closed");
      setDetailOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDeclineRecon = async () => {
    if (!selected?.recon) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/travelling/reconciliations/${selected.recon.id}/finance-approve?approval=declined&notes=${encodeURIComponent(notes)}`,
        {}, { headers: getHeaders() }
      );
      toast.success("Reconciliation declined");
      setDetailOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleOverride = async (balanceId) => {
    if (!overrideForm.override_letter_url.trim()) { toast.error("Override letter URL required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/travelling/outstanding-balances/${balanceId}/override`, overrideForm, { headers: getHeaders() });
      toast.success("Balance overridden");
      setOverrideOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/travelling/export/disbursements`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "travelling_disbursements.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Landmark className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">FINANCE</h1>
            <p className="text-slate-600">Approvals, disbursements & outstanding balances</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 flex-wrap">
        {["claims", "recons", "balances"].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {tab === "claims" ? `Pending Claims (${pendingClaims.length})` :
             tab === "recons" ? `Pending Recons (${pendingRecons.length})` :
             `Balances (${balances.length})`}
          </button>
        ))}
      </div>

      {/* Pending Claims */}
      {activeTab === "claims" && (
        loading ? <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div> :
        pendingClaims.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No claims pending finance approval.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-app border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Employee</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Destination</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Amount</th>
                  <th className="text-left p-3 font-semibold text-slate-600"></th>
                </tr>
              </thead>
              <tbody>
                {pendingClaims.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                    <td className="p-3 font-medium text-slate-800">{t.employee_name}</td>
                    <td className="p-3 text-slate-700">{t.destination}</td>
                    <td className="p-3 text-slate-700">{t.currency} {t.estimated_cost?.toLocaleString()}</td>
                    <td className="p-3">
                      <Button size="sm" onClick={() => openClaimDetail(t)} className="rounded-none bg-sky-600 hover:bg-sky-500 text-white">
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Pending Reconciliations */}
      {activeTab === "recons" && (
        loading ? <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div> :
        pendingRecons.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No reconciliations pending finance approval.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-app border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Employee</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Destination</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Status</th>
                  <th className="text-left p-3 font-semibold text-slate-600"></th>
                </tr>
              </thead>
              <tbody>
                {pendingRecons.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                    <td className="p-3 font-medium text-slate-800">{t.employee_name}</td>
                    <td className="p-3 text-slate-700">{t.destination}</td>
                    <td className="p-3"><span className="text-xs px-2 py-0.5 text-indigo-600 bg-indigo-50">Manager Rec.</span></td>
                    <td className="p-3">
                      <Button size="sm" onClick={() => openReconDetail(t)} className="rounded-none bg-sky-600 hover:bg-sky-500 text-white">
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Outstanding Balances */}
      {activeTab === "balances" && (
        loading ? <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div> :
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-600">Employee</th>
                <th className="text-left p-3 font-semibold text-slate-600">Amount</th>
                <th className="text-left p-3 font-semibold text-slate-600">Currency</th>
                <th className="text-left p-3 font-semibold text-slate-600">Blocked</th>
                <th className="text-left p-3 font-semibold text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {balances.length === 0 ? (
                <tr><td colSpan={5} className="p-16 text-center text-slate-500">No outstanding balances.</td></tr>
              ) : balances.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3 font-medium text-slate-800">{b.employee_name}</td>
                  <td className="p-3 text-slate-700">{b.amount?.toLocaleString()}</td>
                  <td className="p-3 text-slate-700">{b.currency}</td>
                  <td className="p-3">
                    {b.blocked ? (
                      <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 flex items-center gap-1 w-fit">
                        <AlertTriangle className="w-3 h-3" /> Blocked
                      </span>
                    ) : (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5">Overridden</span>
                    )}
                  </td>
                  <td className="p-3">
                    {b.blocked && (
                      <Button size="sm" variant="outline" onClick={() => { setOverrideForm({ override_letter_url: "", override_notes: "" }); setSelected({ ...b, type: "override" }); setOverrideOpen(true); }}
                        className="rounded-none border-amber-300 text-amber-600">
                        Override
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Claim Detail Dialog */}
      <Dialog open={detailOpen && selected?.type === "claims"} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Review Claim — {selected?.trip?.employee_name}</DialogTitle></DialogHeader>
          {selected?.claim && (
            <div className="space-y-3 mt-2">
              <div className="bg-app p-3 border border-slate-200 text-sm space-y-1">
                <p><span className="text-slate-500">Destination:</span> {selected.trip.destination}</p>
                <p><span className="text-slate-500">GL Account:</span> {selected.claim.gl_account}</p>
                <p><span className="text-slate-500">Daily S&T:</span> {selected.claim.daily_s_and_t_amount}</p>
                <p><span className="text-slate-500">Total Claim:</span> <strong>{selected.claim.total_claim_amount}</strong></p>
                <p><span className="text-slate-500">Manager:</span> <span className="text-green-600">{selected.claim.manager_recommendation}</span></p>
                <p><span className="text-slate-500">Manager Notes:</span> {selected.claim.manager_notes || "-"}</p>
              </div>

              {selected.trip.status === "finance_approved" && (
                <div className="bg-green-50 p-3 border border-green-200 text-sm">
                  <p className="font-medium text-green-700">✅ Claim approved — ready for disbursement</p>
                  <Button size="sm" onClick={() => openDisburse(selected.claim, selected.trip)} className="mt-2 bg-green-600 hover:bg-green-500 rounded-none text-white">
                    <DollarSign className="w-4 h-4 mr-1" /> Disburse Funds
                  </Button>
                </div>
              )}

              {selected.trip.status === "manager_recommended" && (
                <>
                  <textarea placeholder="Finance notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
                  <div className="flex gap-2">
                    <Button onClick={handleApproveClaim} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-500 rounded-none text-white">
                      <CheckCircle className="w-4 h-4 mr-1" /> Approve Claim
                    </Button>
                    <Button onClick={handleDeclineClaim} disabled={saving} variant="outline" className="flex-1 rounded-none border-red-300 text-red-600">
                      <XCircle className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reconciliation Detail Dialog */}
      <Dialog open={detailOpen && selected?.type === "recons"} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Review Reconciliation — {selected?.trip?.employee_name}</DialogTitle></DialogHeader>
          {selected?.recon && (
            <div className="space-y-3 mt-2">
              <div className="bg-app p-3 border border-slate-200 text-sm space-y-1">
                <p><span className="text-slate-500">Destination:</span> {selected.trip.destination}</p>
                <p><span className="text-slate-500">Amount Used:</span> {selected.recon.total_amount_used}</p>
                <p><span className="text-slate-500">Amount Returned:</span> {selected.recon.total_amount_returned}</p>
                <p><span className="text-slate-500">Over:</span> <span className="text-green-600">{selected.recon.over_amount}</span></p>
                <p><span className="text-slate-500">Under:</span> <span className="text-red-600">{selected.recon.under_amount}</span></p>
                <p><span className="text-slate-500">AI Verified:</span> {selected.recon.ai_verified ? "✅" : "❌"}</p>
                <p><span className="text-slate-500">AI Notes:</span> {selected.recon.ai_verification_notes || "-"}</p>
                <p><span className="text-slate-500">AI Suspicious:</span> {selected.recon.ai_suspicious ? "⚠️ Yes" : "✅ No"}</p>
                <p><span className="text-slate-500">Manager:</span> <span className="text-green-600">{selected.recon.manager_recommendation}</span></p>
                <p><span className="text-slate-500">Manager Notes:</span> {selected.recon.manager_notes || "-"}</p>
              </div>
              <textarea placeholder="Finance notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
              <div className="flex gap-2">
                <Button onClick={handleApproveRecon} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-500 rounded-none text-white">
                  <CheckCircle className="w-4 h-4 mr-1" /> Approve & Close Trip
                </Button>
                <Button onClick={handleDeclineRecon} disabled={saving} variant="outline" className="flex-1 rounded-none border-red-300 text-red-600">
                  <XCircle className="w-4 h-4 mr-1" /> Decline
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disbursement Dialog */}
      <Dialog open={disburseOpen} onOpenChange={setDisburseOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Disburse Funds</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">Amount to disburse: <strong>{selected?.trip?.currency} {selected?.claim?.total_claim_amount?.toLocaleString()}</strong></p>
            <Input placeholder="Payment Reference" value={disburseForm.payment_reference} onChange={(e) => setDisburseForm(f => ({ ...f, payment_reference: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <textarea placeholder="Notes" value={disburseForm.notes} onChange={(e) => setDisburseForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} />
            <Button onClick={handleDisburse} disabled={saving} className="w-full bg-green-600 hover:bg-green-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><Send className="w-4 h-4 mr-2" /> Confirm Disbursement</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Override Outstanding Balance</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">Employee: <strong>{selected?.employee_name}</strong> | Amount: {selected?.currency} {selected?.amount?.toLocaleString()}</p>
            <Input placeholder="Override Letter URL" value={overrideForm.override_letter_url} onChange={(e) => setOverrideForm(f => ({ ...f, override_letter_url: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <textarea placeholder="Notes" value={overrideForm.override_notes} onChange={(e) => setOverrideForm(f => ({ ...f, override_notes: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} />
            <Button onClick={() => handleOverride(selected?.id)} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : "Override Block"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Button */}
      <div className="mt-6">
        <Button onClick={handleExport} variant="outline" className="rounded-none border-slate-300">
          <Download className="w-4 h-4 mr-1" /> Export Disbursements CSV
        </Button>
      </div>
    </div>
  );
}
